import React, { useEffect, useState } from 'react';
import {
  Box,
  Icon,
  SidePanel,
  TabContent,
  TabGroup,
  TabItem,
  Typography,
} from '@saviynt/design-system';

import { type User } from '../../../services';
import { useInvertedLocalize } from '../../../utils/localization';

import { type EntitlementRow, type LocalizeFn } from './IncludedEntitlements';
import invertedMessages from './messages';

import './RoleSidePanel.css';

const NULL_TEXT = '-';

// Helper function to convert boolean accessStatus to string
const getAccessStatusText = (l: LocalizeFn, accessStatus?: boolean): string => {
  if (accessStatus === true) {
    return l('Present');
  }

  return l('Absent');
};

interface RoleSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  data: User | EntitlementRow | null;
  type: 'user' | 'entitlement';
}

function UserSidePanelContent({ user }: { user: User }) {
  const l = useInvertedLocalize(invertedMessages);

  return (
    <Box className='Roles-RoleSidePanel-content'>
      <Box className='Roles-RoleSidePanel-section'>
        <Typography kind='h4' className='Roles-RoleSidePanel-sectionTitle'>
          {l('Attributes')}
        </Typography>
      </Box>

      {/* User Information */}
      <Box className='Roles-RoleSidePanel-section'>
        <Box className='Roles-RoleSidePanel-field'>
          <Typography kind='body2' className='Roles-RoleSidePanel-fieldLabel'>
            {l('User')}
          </Typography>
          <Typography kind='body3' className='Roles-RoleSidePanel-fieldValue'>
            {/* Show available name parts or dash if both are null */}
            {[user.firstname, user.lastname].filter(Boolean).join(' ') ||
              NULL_TEXT}
          </Typography>
        </Box>

        <Box className='Roles-RoleSidePanel-field'>
          <Typography kind='body2' className='Roles-RoleSidePanel-fieldLabel'>
            {l('Username')}
          </Typography>
          <Typography kind='body3' className='Roles-RoleSidePanel-fieldValue'>
            {user.username ?? NULL_TEXT}
          </Typography>
        </Box>

        <Box className='Roles-RoleSidePanel-field'>
          <Typography kind='body2' className='Roles-RoleSidePanel-fieldLabel'>
            {l('Employee Type')}
          </Typography>
          <Typography kind='body3' className='Roles-RoleSidePanel-fieldValue'>
            {user.employeetype ?? NULL_TEXT}
          </Typography>
        </Box>

        <Box className='Roles-RoleSidePanel-field'>
          <Typography kind='body2' className='Roles-RoleSidePanel-fieldLabel'>
            {l('Job Title')}
          </Typography>
          <Typography kind='body3' className='Roles-RoleSidePanel-fieldValue'>
            {user.title || NULL_TEXT}
          </Typography>
        </Box>

        <Box className='Roles-RoleSidePanel-field'>
          <Typography kind='body2' className='Roles-RoleSidePanel-fieldLabel'>
            {l('Department')}
          </Typography>
          <Typography kind='body3' className='Roles-RoleSidePanel-fieldValue'>
            {user.departmentname ?? NULL_TEXT}
          </Typography>
        </Box>

        <Box className='Roles-RoleSidePanel-field'>
          <Typography kind='body2' className='Roles-RoleSidePanel-fieldLabel'>
            {l('Manager')}
          </Typography>
          <Typography kind='body3' className='Roles-RoleSidePanel-fieldValue'>
            {user.manager || NULL_TEXT}
          </Typography>
        </Box>

        <Box className='Roles-RoleSidePanel-field'>
          <Typography kind='body2' className='Roles-RoleSidePanel-fieldLabel'>
            {l('Owner')}
          </Typography>
          <Typography kind='body3' className='Roles-RoleSidePanel-fieldValue'>
            {user.owner ?? NULL_TEXT}
          </Typography>
        </Box>
      </Box>

      <Box className='Roles-RoleSidePanel-field'>
        <Typography kind='body2' className='Roles-RoleSidePanel-fieldLabel'>
          {l('Country')}
        </Typography>
        <Typography kind='body3' className='Roles-RoleSidePanel-fieldValue'>
          {user.country ?? NULL_TEXT}
        </Typography>
      </Box>

      <Box className='Roles-RoleSidePanel-field'>
        <Typography kind='body2' className='Roles-RoleSidePanel-fieldLabel'>
          {l('City')}
        </Typography>
        <Typography kind='body3' className='Roles-RoleSidePanel-fieldValue'>
          {user.city ?? NULL_TEXT}
        </Typography>
      </Box>

      <Box className='Roles-RoleSidePanel-field'>
        <Typography kind='body2' className='Roles-RoleSidePanel-fieldLabel'>
          {l('Cost Center')}
        </Typography>
        <Typography kind='body3' className='Roles-RoleSidePanel-fieldValue'>
          {user.costcenter ?? NULL_TEXT}
        </Typography>
      </Box>

      <Box className='Roles-RoleSidePanel-field'>
        <Typography kind='body2' className='Roles-RoleSidePanel-fieldLabel'>
          {l('Access Status')}
        </Typography>
        <Typography kind='body3' className='Roles-RoleSidePanel-fieldValue'>
          {getAccessStatusText(l, user.accessStatus)}
        </Typography>
      </Box>
    </Box>
  );
}

// Component for entitlement details content
function EntitlementSidePanelContent({
  entitlement,
}: {
  entitlement: EntitlementRow;
}) {
  const l = useInvertedLocalize(invertedMessages);

  return (
    <Box className='Roles-RoleSidePanel-content'>
      <Box className='Roles-RoleSidePanel-section'>
        <Typography kind='h4' className='Roles-RoleSidePanel-sectionTitle'>
          {l('Attributes')}
        </Typography>
      </Box>

      {/* Entitlement Information */}
      <Box className='Roles-RoleSidePanel-section'>
        <Box className='Roles-RoleSidePanel-field'>
          <Typography kind='body2' className='Roles-RoleSidePanel-fieldLabel'>
            {l('Entitlement Type')}
          </Typography>
          <Typography kind='body3' className='Roles-RoleSidePanel-fieldValue'>
            {entitlement.entitlementDisplayName ||
              entitlement.entitlementTypeName ||
              NULL_TEXT}
          </Typography>
        </Box>

        <Box className='Roles-RoleSidePanel-field'>
          <Typography kind='body2' className='Roles-RoleSidePanel-fieldLabel'>
            {l('Entitlement Description')}
          </Typography>
          <Typography kind='body3' className='Roles-RoleSidePanel-fieldValue'>
            {entitlement.evDescription ?? NULL_TEXT}
          </Typography>
        </Box>

        <Box className='Roles-RoleSidePanel-field'>
          <Typography kind='body2' className='Roles-RoleSidePanel-fieldLabel'>
            {l('Entitlement Glossary')}
          </Typography>
          <Typography kind='body3' className='Roles-RoleSidePanel-fieldValue'>
            {entitlement.entitlementGlossary ?? NULL_TEXT}
          </Typography>
        </Box>

        <Box className='Roles-RoleSidePanel-field'>
          <Typography kind='body2' className='Roles-RoleSidePanel-fieldLabel'>
            {l('Entitlement Privileged')}
          </Typography>
          <Typography kind='body3' className='Roles-RoleSidePanel-fieldValue'>
            {entitlement.entitlementPrivileged ?? NULL_TEXT}
          </Typography>
        </Box>

        <Box className='Roles-RoleSidePanel-field'>
          <Typography kind='body2' className='Roles-RoleSidePanel-fieldLabel'>
            {l('Endpoint Name')}
          </Typography>
          <Typography kind='body3' className='Roles-RoleSidePanel-fieldValue'>
            {entitlement.endpointDisplayName ??
              (entitlement.endpointName || NULL_TEXT)}
          </Typography>
        </Box>

        <Box className='Roles-RoleSidePanel-field'>
          <Typography kind='body2' className='Roles-RoleSidePanel-fieldLabel'>
            {l('System Name')}
          </Typography>
          <Typography kind='body3' className='Roles-RoleSidePanel-fieldValue'>
            {entitlement.securitySystemDisplayName ??
              entitlement.securitySystemName ??
              NULL_TEXT}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function RoleSidePanel({ isOpen, onClose, data, type }: RoleSidePanelProps) {
  const l = useInvertedLocalize(invertedMessages);
  const [activeTab, setActiveTab] = useState('summary');

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    };

    if (isOpen) {
      // Use capture phase to ensure it runs before child handlers
      document.addEventListener('keydown', handleEscapeKey, true);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey, true);
    };
  }, [isOpen, onClose]);

  if (!data) {
    return null;
  }

  // side panel header config
  const getHeaderConfig = () => {
    if (type === 'user') {
      const user = data as User;

      // if firstname and lastname are both null, show username
      return {
        title:
          [user.firstname, user.lastname].filter(Boolean).join(' ') ||
          user.username,
        description: l('User'),
        headerIcon: <Icon kind='User' size='xxLarge' />,
      };
    }

    const entitlement = data as EntitlementRow;

    return {
      title: entitlement.evDisplayName ?? entitlement.entitlementValueName,
      description: entitlement.evDescription ?? '-',
      headerIcon: (
        <Icon
          kind='Entitlement'
          size='xxLarge'
          className='Roles-RoleSidePanel-headerIcon'
        />
      ),
    };
  };

  const layout = (
    <div className='Roles-RoleSidePanel-layoutWidth'>
      <TabGroup value={activeTab} onChange={setActiveTab}>
        <TabItem
          label={l('Summary')}
          value='summary'
          icon={<Icon kind='Document' size='smallMedium' />}
        />
      </TabGroup>
      <TabContent value={activeTab} tabValue='summary'>
        {type === 'user' ? (
          <UserSidePanelContent user={data as User} />
        ) : (
          <EntitlementSidePanelContent entitlement={data as EntitlementRow} />
        )}
      </TabContent>
    </div>
  );

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      header={getHeaderConfig()}
      layout={layout}
      className='Roles-RoleSidePanel'
      classNames={{ layoutClassName: 'Roles-RoleSidePanel-layout' }}
    />
  );
}

export default RoleSidePanel;
